/**
 * Pure state derivation for the MCAP HITL approval card.
 *
 * The card can be rendered in three very different situations:
 *  1. Live suspension — the workflow just suspended in this browser session.
 *  2. Live post-approval — the user approved here and the recording-status
 *     SSE stream drives per-job progress through Redux.
 *  3. Re-hydrated conversation — a past thread was reopened (possibly in a
 *     different browser); the only authoritative signal is the persisted
 *     workflow node (`copilot_workflow_states.current_node`).
 *
 * Priority when deriving the view: local approval (this session) →
 * persisted workflow node (server truth) → localStorage snapshot (job-level
 * tracking supplement) → approval prompt.
 */

import type { MCAPSuspensionPayload } from "../../hooks/useCopilotRuntime";
import type {
  RecordingUploadJob,
  RecordingUploadStatus,
} from "../../store/slices/recordingUploadSlice";

// ─── Types ────────────────────────────────────────────────────────────────────

/** Local persistence so live job tracking survives a reload / thread reopen. */
export interface PersistedApproval {
  batchId: string;
  jobIds: string[];
  status: RecordingUploadStatus;
}

export type McapCardView =
  | { mode: "approval" }
  | { mode: "live"; approval: PersistedApproval };

export type OverallKind =
  | "QUEUED_OFFLINE"
  | "PENDING"
  | "UPLOADING"
  | "RETRYING"
  | "UPLOADED"
  | "FAILED";

export interface OverallState {
  kind: OverallKind;
  progress: number;
  done: number;
  total: number;
  reason?: string;
}

// ─── Workflow-node interpretation ────────────────────────────────────────────

/** Nodes proving the upload itself completed successfully. */
const UPLOAD_SUCCEEDED_NODES = new Set([
  "MCAP_UPLOADED",
  "ANALYSIS_READY",
  "ANALYZING",
  "ANALYSIS_COMPLETE",
  "ANALYSIS_FAILED",
]);

/** Post-approval nodes for uploads still in flight (node → job status). */
const UPLOAD_IN_FLIGHT_NODES: Record<string, RecordingUploadStatus> = {
  PENDING_EDGE_ACK: "PENDING_EDGE_ACK",
  QUEUED: "QUEUED",
  QUEUED_OFFLINE: "QUEUED_OFFLINE",
  UPLOADING: "UPLOADING",
  RETRYING: "RETRYING",
};

/**
 * Maps the persisted workflow node onto an upload status, or `undefined`
 * when the node carries no post-approval information (suspended, IDLE, or
 * not hydrated at all during a live run).
 */
export function statusFromWorkflowNode(
  node: string | undefined,
): RecordingUploadStatus | undefined {
  if (!node) return undefined;
  if (UPLOAD_SUCCEEDED_NODES.has(node)) return "UPLOADED";
  if (node === "MCAP_UPLOAD_FAILED") return "FAILED";
  return UPLOAD_IN_FLIGHT_NODES[node];
}

/**
 * True when the post-upload deep analysis is already owned by another path
 * (the drawer's headless resume, or it already ran) so the card must not
 * signal the runtime adapter through the analysis bridge again.
 */
export function analysisHandledByWorkflow(node: string | undefined): boolean {
  return (
    node === "ANALYSIS_READY" ||
    node === "ANALYZING" ||
    node === "ANALYSIS_COMPLETE" ||
    node === "ANALYSIS_FAILED"
  );
}

export function normalizeStatus(raw: string): RecordingUploadStatus {
  const known: RecordingUploadStatus[] = [
    "QUEUED",
    "QUEUED_OFFLINE",
    "PENDING_EDGE_ACK",
    "UPLOADING",
    "RETRYING",
    "UPLOADED",
    "FAILED",
    "DOWNLOADING",
  ];
  return known.includes(raw as RecordingUploadStatus)
    ? (raw as RecordingUploadStatus)
    : "QUEUED";
}

// ─── localStorage persistence ────────────────────────────────────────────────

function persistenceKey(payload: MCAPSuspensionPayload): string {
  return `insaion:mcap-approval:${payload.thread_id ?? "nothread"}:${payload.incident_id}`;
}

export function loadPersistedApproval(
  payload: MCAPSuspensionPayload,
): PersistedApproval | null {
  try {
    const raw = window.localStorage.getItem(persistenceKey(payload));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedApproval;
    if (!parsed?.batchId || !Array.isArray(parsed.jobIds)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function savePersistedApproval(
  payload: MCAPSuspensionPayload,
  approval: PersistedApproval,
): void {
  try {
    window.localStorage.setItem(
      persistenceKey(payload),
      JSON.stringify(approval),
    );
  } catch {
    /* non-fatal */
  }
}

// ─── View derivation ─────────────────────────────────────────────────────────

/**
 * Decides whether the card shows the approval prompt or the live/settled
 * extraction view. Signals in priority order:
 *  1. `analysisPresent` — findings already sit after this suspension in the
 *     transcript, so the extraction definitely completed (message-scoped;
 *     survives workflow-node overwrites by later runs in the thread).
 *  2. Local approval (this browser) — live job tracking, with a terminal
 *     workflow node overriding the frozen localStorage status.
 *  3. Workflow node — authoritative server state; once it moved past the
 *     suspension the approval prompt must never re-render, even without a
 *     localStorage record of the approval.
 */
export function deriveCardView(
  approval: PersistedApproval | null,
  workflowNode: string | undefined,
  analysisPresent = false,
): McapCardView {
  if (analysisPresent) {
    return {
      mode: "live",
      approval: {
        batchId: approval?.batchId ?? "",
        jobIds: approval?.jobIds ?? [],
        status: "UPLOADED",
      },
    };
  }

  const nodeStatus = statusFromWorkflowNode(workflowNode);

  if (approval) {
    // Terminal node wins over the localStorage snapshot, which is frozen at
    // approval time (e.g. PENDING_EDGE_ACK) once the live SSE stream is gone.
    const settled =
      nodeStatus === "UPLOADED" || nodeStatus === "FAILED"
        ? nodeStatus
        : undefined;
    return {
      mode: "live",
      approval: settled ? { ...approval, status: settled } : approval,
    };
  }

  if (nodeStatus) {
    // Approval already granted server-side but unknown to this browser —
    // synthesize a job-less view driven purely by the workflow node.
    return {
      mode: "live",
      approval: { batchId: "", jobIds: [], status: nodeStatus },
    };
  }

  return { mode: "approval" };
}

// ─── Aggregate progress ──────────────────────────────────────────────────────

/**
 * Folds per-job Redux tracking into one aggregate. When the approval carries
 * no job ids (view synthesized from the workflow node alone) the fallback
 * status acts as a single pseudo-job.
 */
export function computeOverallState(
  approval: PersistedApproval,
  activeUploads: Record<string, RecordingUploadJob>,
  completedRecently: string[],
): OverallState {
  const jobIds = approval.jobIds;
  const total = jobIds.length || 1;
  let done = 0;
  let failed = 0;
  let uploading = 0;
  let retrying = 0;
  let offline = 0;
  let progressSum = 0;
  let reason: string | undefined;

  const statuses: Array<{ status: RecordingUploadStatus; progress: number }> =
    jobIds.length > 0
      ? jobIds.map((jobId) => {
          const job = activeUploads[jobId];
          const isCompleted = completedRecently.includes(jobId);
          if (job?.reason) reason = job.reason;
          return {
            status: job
              ? job.status
              : isCompleted
                ? "UPLOADED"
                : approval.status,
            progress: job ? job.progress : isCompleted ? 100 : 0,
          };
        })
      : [{ status: approval.status, progress: 0 }];

  for (const { status, progress } of statuses) {
    switch (status) {
      case "UPLOADED":
        done += 1;
        progressSum += 100;
        break;
      case "FAILED":
      case "CANCELLED":
        failed += 1;
        break;
      case "UPLOADING":
      case "DOWNLOADING":
        uploading += 1;
        progressSum += progress;
        break;
      case "RETRYING":
        retrying += 1;
        progressSum += progress;
        break;
      case "QUEUED_OFFLINE":
        offline += 1;
        break;
      default:
        break;
    }
  }

  let kind: OverallKind;
  if (failed > 0) kind = "FAILED";
  else if (done === total) kind = "UPLOADED";
  else if (uploading > 0) kind = "UPLOADING";
  else if (retrying > 0) kind = "RETRYING";
  else if (offline > 0) kind = "QUEUED_OFFLINE";
  else kind = "PENDING";

  return {
    kind,
    progress: Math.round(progressSum / total),
    done,
    total,
    reason,
  };
}
